import { useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { CitationItem } from './Interface';
import { logDatadogRumCustomAction } from 'op-utils/datadogRumCustomAction';

interface PillCitationsProps {
  items: CitationItem[];
  maxVisible?: number;
  onRelatedItemClick: (item: CitationItem) => void;
  expanded: boolean;
  patientId?: string;
}

// Layout constants for pill sizing and spacing
const PILL_WIDTH = 20; // Base pill width in pixels
const PILL_MARGIN_LEFT = 8; // Left margin for spacing between pills
const PILL_MARGIN_RIGHT = 0; // Right margin (currently no spacing on right)

/**
 * Validates if an item has a valid document type that should be rendered as a pill.
 * Only include types that have actual viewable documents or content.
 * @param item The citation item to validate
 * @returns true if the item should be rendered, false otherwise
 */
const isValidDocumentType = (item: CitationItem): boolean => {
  if (!item?.documentType) return false;

  const docType = item.documentType.toLowerCase();

  // Resource types with viewable documents
  if (['documentpart', 'note', 'pdf', 'image', 'text', 'letter'].includes(docType)) {
    return true;
  }

  // Document type categories from timeline (radiology, pathology, etc.)
  if (
    docType.startsWith('radiology_') ||
    docType.startsWith('pathology_') ||
    docType === 'distress_thermometer' ||
    docType === 'health_assessment'
  ) {
    return true;
  }

  // Exclude structured data types that don't have document views
  // (Appointment, LabResultsPanel, Diagnosis, ROPrescription, ROTreatment, Patient)
  return false;
};

const getPillStyle = (theme: any): React.CSSProperties => ({
  display: 'inline-block',
  textAlign: 'center',
  width: PILL_WIDTH,
  height: PILL_WIDTH,
  borderRadius: PILL_WIDTH / 2,
  background: theme.palette.action.selected,
  color: theme.palette.text.primary,
  marginLeft: `${PILL_MARGIN_LEFT}px`,
  marginRight: PILL_MARGIN_RIGHT,
  fontWeight: 500,
  fontSize: 10,
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'background 0.2s',
  verticalAlign: 'middle',
  lineHeight: `${PILL_WIDTH}px`,
});

const PillCitations: React.FC<PillCitationsProps> = ({
  items,
  maxVisible = 3,
  onRelatedItemClick,
  expanded,
  patientId,
}) => {
  const [fullyExpanded, setFullyExpanded] = useState(false);
  const theme = useTheme();
  const pillStyle = useMemo(() => getPillStyle(theme), [theme]);

  // Filter for valid document types first, preserving original indices
  const validItemsWithIndices = useMemo(
    () => items.map((item, index) => ({ item, originalIndex: index })).filter(({ item }) => isValidDocumentType(item)),
    [items],
  );

  const validItems = validItemsWithIndices.map(({ item }) => item);
  const hasMultipleItems = validItems.length > 1;
  const showCollapse = validItems.length > maxVisible && fullyExpanded;
  const hasSelectedItem = validItems.some((item) => item.selected);

  const handleExpand = () => {
    if (!fullyExpanded) {
      logDatadogRumCustomAction('ai_citation_pills_expand', {
        patientId,
      });
      setFullyExpanded(true);
    }
  };

  const handleCollapse = () => {
    if (fullyExpanded) {
      setFullyExpanded(false);
    }
  };

  // Slice the filtered valid items
  let pillsToShow = validItemsWithIndices;
  if (!fullyExpanded && validItems.length > maxVisible) {
    pillsToShow = validItemsWithIndices.slice(0, maxVisible);
  }

  return (
    <div style={{ display: 'inline-block', position: 'relative' }}>
      <div
        style={{
          ...pillStyle,
          transition: 'all 0.4s ease-in-out',
          opacity: !expanded && !hasSelectedItem && hasMultipleItems ? 1 : 0,
          visibility: !expanded && !hasSelectedItem && hasMultipleItems ? 'visible' : 'hidden',
          position: 'absolute',
          left: 0,
        }}
        tabIndex={0}
        data-testid="pill-collapsed"
        onClick={handleExpand}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleExpand()}
        aria-label="Expand citations">
        &gt;
      </div>
      <span
        style={{
          display: 'inline-block',
          transition: 'all 0.6s ease-in-out',
          opacity: expanded || hasSelectedItem || validItems.length === 1 ? 1 : 0,
          whiteSpace: 'nowrap',
          visibility: expanded || hasSelectedItem || validItems.length === 1 ? 'visible' : 'hidden',
        }}>
        {pillsToShow.map(({ item, originalIndex }) => {
          const background = item.selected ? theme.palette.action.active : pillStyle.background;

          return (
            <div
              key={originalIndex}
              style={{ ...pillStyle, background }}
              tabIndex={0}
              data-testid={`pill-${originalIndex + 1}`}
              onClick={() => onRelatedItemClick(item)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onRelatedItemClick(item)}
              aria-label={`Citation ${originalIndex + 1}`}>
              {originalIndex + 1}
            </div>
          );
        })}
        {!fullyExpanded && validItems.length > maxVisible && (
          <div
            style={pillStyle}
            tabIndex={0}
            data-testid="pill-expand"
            onClick={handleExpand}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleExpand()}
            aria-label="Expand all citations">
            ...
          </div>
        )}
        {showCollapse && (
          <div
            style={pillStyle}
            tabIndex={0}
            data-testid="pill-collapse"
            onClick={handleCollapse}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleCollapse()}
            aria-label="Collapse citations">
            «
          </div>
        )}
      </span>
    </div>
  );
};

export default PillCitations;
